/**
 * Combined primary-action button with an optional dropdown menu for additional options.
 *
 * @module components/common/SplitButton
 * @author Snowfly
 * @version 1.0.0
 */
import React, {useEffect} from 'react';
import Button from '@material-ui/core/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Paper from '@material-ui/core/Paper';
import Popper from '@material-ui/core/Popper';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import { makeStyles } from '@material-ui/core/styles';
import SplitButtonStyle from './SplitButton.js';
import PropTypes from 'prop-types';

/*
    TODO - Add a property that allows creating a custom Popper (eg. pass a custom Popup menu)
 */
/**
 * A SplitButton component that consists of a main button and a dropdown for additional options.
 * This component allows selection of options and an associated callback for handling specific actions.
 *
 * @param {Object} props - The properties passed to the SplitButton component.
 * @param {Array} props.options - An array of objects representing the available options for the dropdown. Each object can contain properties like `text`, `disabled`, `callback`, and `data`.
 * @param {Function} props.onItemSelected - Callback function triggered when a dropdown option is selected. Receives the selected index and option as arguments.
 * @param {React.ReactNode} props.mainButtonIcon - An icon to display on the main button.
 * @param {Function} props.onMainButtonClick - Callback function triggered when the main button is clicked.
 * @param {string} props.mainButtonText - The text to display on the main button.
 * @param {Object} props.extraStyles - Additional styles to apply to the root element of the SplitButton component.
 *
 * @return {React.ReactElement} The rendered SplitButton component.
 */
const SplitButton = (props) => {
    const useStyles = makeStyles((theme) => SplitButtonStyle(theme));
    const classes = useStyles();

    const options = props?.options,
        onItemSelected = props?.onItemSelected,
        mainButtonIcon = props?.mainButtonIcon,
        onMainButtonClick = props?.onMainButtonClick,
        mainButtonText = props?.mainButtonText,
        extraStyles = props?.extraStyles;

    const anchorRef = React.useRef(null);
    const [selectedIndex, setSelectedIndex] = React.useState(0);
    const [open, setOpen] = React.useState(false);
    const [currentOption, setCurrentOption] = React.useState(0);
    const [itemClicked, setItemClicked] = React.useState(-1);

    useEffect(
        () => {
            if (open && (itemClicked > -1)) {
                setOpen(false);
                setCurrentOption(selectedIndex);
                onItemSelected(selectedIndex, options[selectedIndex]);

            }
        },
        [selectedIndex, options, onItemSelected, open, itemClicked, setOpen]
    );

    /**
     * Handles the click event on a menu item.
     *
     * @param {Object} event - The event object associated with the click event.
     * @param {number} index - The index of the menu item that was clicked.
     *
     * This function performs the following actions:
     * - Updates the state to mark the clicked item's index.
     * - If the clicked item's index is different from the currently selected index, updates the selected index state.
     * - If the clicked item's index is the same as the currently selected index, closes the menu and triggers the item selection callback.
     */
    const handleMenuItemClick = (event, index) => {
        setItemClicked(index);
        if (index !== selectedIndex) {
            setSelectedIndex(index);
        } else {
            setOpen(false);
            onItemSelected(selectedIndex, options[selectedIndex]);
        }
    };

    /**
     * Toggles the open state of a component and resets the itemClicked value.
     *
     * This function performs two main actions:
     * 1. Resets the itemClicked state to its default value of -1.
     * 2. Toggles the boolean state variable `open` by inverting its current value.
     *
     * Typically used to handle the logic for opening and closing a UI component
     * such as a dropdown, modal, or sidebar while clearing any selected item.
     */
    const handleToggle = () => {
        setItemClicked(-1);
        setOpen((prevOpen) => !prevOpen);
    };

    /**
     * Handles closing of a component or element, typically used for menu or overlay interactions.
     *
     * This function prevents closing if the clicked target is contained within the referenced anchor element.
     * If the clicked target is outside the referenced anchor, it triggers a state update to close the element.
     *
     * @param {Object} event - The event object representing the user's interaction.
     */
    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }

        setOpen(false);
    };

    return (
        <span className={classes.root} style={extraStyles}>
            <ButtonGroup variant="contained"
                color="primary"
                ref={anchorRef}
                aria-label="split button"
                className={classes.splitButton}
                style={{flexWrap: 'nowrap'}}
            >
                <Button variant="contained"
                    className={classes.button}
                    size="small"
                    onClick={onMainButtonClick}
                    startIcon={mainButtonIcon}
                >
                    {mainButtonText}
                </Button>
                {options && options.length ? (
                <Button variant="contained"
                    className={classes.dropdown}
                    size="small"
                    onClick={handleToggle}
                    aria-controls={open ? 'split-button-menu' : undefined}
                    aria-expanded={open ? 'true' : undefined}
                >
                    <ArrowDropDownIcon />
                </Button>
                ): null }
            </ButtonGroup>
            {options && options.length ? (
            <Popper open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                placement="left"
                className={classes.popper}
                disablePortal={true}
                modifiers={{
                    flip: {
                        enabled: true,
                    },
                    preventOverflow: {
                        enabled: true,
                        boundariesElement: "scrollParent",
                    },
                    arrow: {
                        enabled: false,
                    },
                }}
            >
                <Paper>
                    <ClickAwayListener onClickAway={handleClose}>
                        <MenuList id="split-button-menu">
                            {options.map((option, index) => (
                                <MenuItem
                                    id={`menuOption${index}`}
                                    key={`menuOption${index}`}
                                    disabled={option.disabled ?? false}
                                    selected={currentOption === index}
                                    onClick={(event) => handleMenuItemClick(event, index)}
                                >
                                    {option.callback ? option.callback(option, option.data ?? null) : option.text}
                                </MenuItem>
                            ))}
                        </MenuList>
                    </ClickAwayListener>
                </Paper>
            </Popper>
            ) : null}
        </span>
    );
}

SplitButton.propTypes = {
    options: PropTypes.arrayOf(
        PropTypes.shape({
            text: PropTypes.string,
            disabled: PropTypes.bool,
            callback: PropTypes.func,
            data: PropTypes.any,
        })
    ).isRequired,
    onItemSelected: PropTypes.func,
    mainButtonIcon: PropTypes.node,
    onMainButtonClick: PropTypes.func,
    mainButtonText: PropTypes.string.isRequired,
    extraStyles: PropTypes.object,
};

export default SplitButton;
